import '@vaadin/text-field';
// eslint-disable-next-line no-duplicate-imports
import type { TextFieldValueChangedEvent, TextField as VaadinTextField } from '@vaadin/text-field';
import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { Ref, ref } from 'lit/directives/ref.js';
import { circleCloseFilledIcon, searchIcon } from '../../../assets/icons';
import { TmInputFieldBase } from '../../tm-input-field.base';

export const tagName = 'tm-list-header-search-field';
@customElement(tagName)
export class TmListHeaderSearchField extends TmInputFieldBase<string> {
    @property({ type: String })
    public allowedCharacters?: string;

    @property({ type: Boolean })
    public clearButton = false;

    @property({ type: Boolean, reflect: true })
    public large = false;

    private get textField() {
        return (this.inputFieldRef as Ref<VaadinTextField>).value;
    }

    protected validate() {
        if (!this.textField) {
            return false;
        }

        return this.textField.checkValidity();
    }

    protected render() {
        const iconColor = this.disabled ? '--input-field-icon-disabled' : '--input-field-icon';
        return html`
            <style>
                vaadin-text-field::part(clear-button)::before {
                    content: url(${`'data:image/svg+xml,${encodeURIComponent(
                        circleCloseFilledIcon.strings[0]
                    )}'`});
                    opacity: 0.6;
                }
            </style>
            <vaadin-text-field
                ${ref(this.inputFieldRef)}
                @value-changed=${(e: TextFieldValueChangedEvent) => {
                    this.changeValue(e.detail.value);
                }}
                part="component"
                class="w-full border border-[--input-field-border] rounded"
                .label=${this.label}
                value=${this.initial || ''}
                placeholder=${ifDefined(this.placeholder)}
                allowed-char-pattern=${ifDefined(this.allowedCharacters)}
                helper-text=${ifDefined(this.helperText)}
                ?disabled=${this.disabled}
                .invalid=${this.invalid}
                ?clear-button-visible=${this.clearButton}
            >
                <slot slot="prefix" name="prefix">
                    <tm-icon .icon=${searchIcon} color="var(${iconColor})" class="mr-1"></tm-icon>
                </slot>
                <slot slot="suffix" name="suffix"></slot>
            </vaadin-text-field>
        `;
    }

    public static get styles() {
        return [
            ...super.styles,
            css`
                :host {
                    position: relative;
                    --input-field-background: var(--container-primary);
                    --input-field-border: rgb(176, 176, 176);
                    --input-field-border-focused: var(--brand-primary);
                    --input-field-border-disabled: var(--tm-color-gray-medium-30);
                    --input-field-icon: var(--tm-color-gray-medium);
                    --input-field-icon-disabled: var(--tm-color-gray-medium-30);
                }
                :host([theme='dark']) {
                    --input-field-background: rgba(8, 20, 39, 0.7);
                    --input-field-border: var(--tm-color-gray-light-30);
                    --input-field-border-focused: var(--brand-100);
                    --input-field-border-disabled: var(--tm-color-gray-light-30);
                    --input-field-icon: var(--brand-100);
                    --input-field-icon-disabled: var(--tm-color-gray-light-30);
                }

                :host::part(input-field) {
                    background: var(--input-field-background);
                }

                vaadin-text-field[disabled] {
                    border-color: var(--input-field-border-disabled);
                }

                :host([large]) {
                    --lumo-size-m: 2.5rem;
                }

                vaadin-text-field[focused] {
                    border-color: var(--input-field-border-focused);
                }

                vaadin-text-field[disabled] {
                    border-color: var(--input-field-border-disabled);
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmListHeaderSearchField;
    }
}
